import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { adminApi, Review } from '../../utils/api'
import {
  ClipboardCheck,
  Check,
  X,
  AlertTriangle,
  Clock,
  Package,
  ChevronDown,
  MessageSquare,
} from 'lucide-react'

type FilterStatus = 'all' | 'submitted' | 'approved' | 'changes_requested' | 'suspended'

// Categories from xarlie.json manifest schema for structured change requests
const CHANGE_CATEGORIES = [
  { id: 'manifest', label: 'Manifest Issues', desc: 'Missing or invalid fields in xarlie.json' },
  { id: 'permissions', label: 'Permissions', desc: 'Excessive or incorrect data_access/capabilities' },
  { id: 'tools', label: 'Skill Definitions', desc: 'Missing input_schema, unclear skill descriptions' },
  { id: 'endpoints', label: 'Endpoints', desc: 'Unreachable or misconfigured MCP endpoints' },
  { id: 'security', label: 'Security', desc: 'Security concerns, credential exposure, SSRF risk' },
  { id: 'naming', label: 'Naming & Branding', desc: 'Misleading name, slug, or summary' },
  { id: 'quality', label: 'Quality', desc: 'Low quality, insufficient documentation, broken functionality' },
  { id: 'policy', label: 'Policy Violation', desc: 'Terms of service or acceptable use violation' },
]

export default function AdminReviewsPage() {
  const { token } = useAuth()
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<FilterStatus>('submitted')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Change request modal state
  const [changeModal, setChangeModal] = useState<string | null>(null) // packageVersionId
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [changeNotes, setChangeNotes] = useState('')

  useEffect(() => {
    loadReviews()
  }, [token, filter])

  async function loadReviews() {
    if (!token) return

    setLoading(true)
    setError(null)

    try {
      const status = filter === 'all' ? undefined : filter
      const response = await adminApi.getReviews(token, status)
      setReviews(response?.items || [])
    } catch (err) {
      console.error('[AdminReviews] Failed to load reviews:', err)
      setError('Failed to load reviews')
      setReviews([])
    } finally {
      setLoading(false)
    }
  }

  async function handleAction(
    packageVersionId: string,
    action: 'approve' | 'request_changes' | 'suspend',
    notes?: string
  ) {
    if (!token) return

    setActionLoading(packageVersionId)

    try {
      await adminApi.applyReviewAction(token, packageVersionId, action, notes)
      await loadReviews()
    } catch (err) {
      console.error('[AdminReviews] Action failed:', err)
      setError('Failed to apply action')
    } finally {
      setActionLoading(null)
    }
  }

  function openChangeRequestModal(packageVersionId: string) {
    setChangeModal(packageVersionId)
    setSelectedCategories([])
    setChangeNotes('')
  }

  function submitChangeRequest() {
    if (!changeModal) return

    const categoryLabels = selectedCategories
      .map((id) => CHANGE_CATEGORIES.find((c) => c.id === id)?.label)
      .filter(Boolean)

    const fullNotes = [
      categoryLabels.length > 0
        ? `Categories: ${categoryLabels.join(', ')}`
        : '',
      changeNotes,
    ]
      .filter(Boolean)
      .join('\n\n')

    handleAction(changeModal, 'request_changes', fullNotes)
    setChangeModal(null)
  }

  function toggleCategory(id: string) {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    )
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'submitted':
        return (
          <span className="badge bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Submitted
          </span>
        )
      case 'approved':
        return (
          <span className="badge badge-approved">
            <Check className="w-3 h-3 mr-1" />
            Approved
          </span>
        )
      case 'changes_requested':
        return (
          <span className="badge bg-orange-100 text-orange-800">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Changes Requested
          </span>
        )
      case 'suspended':
        return (
          <span className="badge bg-red-100 text-red-800">
            <X className="w-3 h-3 mr-1" />
            Suspended
          </span>
        )
      default:
        return (
          <span className="badge bg-gray-100 text-gray-800">{status}</span>
        )
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="text-gray-500 mt-1">
            Review and approve package submissions
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">Filter by status:</span>
          <div className="relative">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterStatus)}
              className="input appearance-none pr-10"
            >
              <option value="all">All</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="changes_requested">Changes Requested</option>
              <option value="suspended">Suspended</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Reviews list */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-gray-500">Loading reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-8 text-center">
            <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No reviews found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Package
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Publisher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reviews.map((review) => (
                  <tr key={review.package_version_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                          <Package className="w-4 h-4 text-gray-400" />
                        </div>
                        <span className="font-medium text-gray-900">
                          {review.package_name || review.package_id}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {review.version}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {review.publisher_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(review.review_status)}
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      {review.notes ? (
                        <p className="text-sm text-gray-600 truncate" title={review.notes}>
                          {review.notes}
                        </p>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {review.submitted_at
                        ? new Date(review.submitted_at).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {review.review_status === 'submitted' && (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() =>
                              handleAction(review.package_version_id, 'approve')
                            }
                            disabled={actionLoading === review.package_version_id}
                            className="btn btn-primary text-xs px-3 py-1"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() =>
                              openChangeRequestModal(review.package_version_id)
                            }
                            disabled={actionLoading === review.package_version_id}
                            className="btn btn-secondary text-xs px-3 py-1 inline-flex items-center gap-1"
                          >
                            <MessageSquare className="w-3 h-3" />
                            Request Changes
                          </button>
                        </div>
                      )}
                      {review.review_status === 'approved' && (
                        <button
                          onClick={() =>
                            handleAction(review.package_version_id, 'suspend')
                          }
                          disabled={actionLoading === review.package_version_id}
                          className="btn text-xs px-3 py-1 text-red-600 border border-red-200 hover:bg-red-50"
                        >
                          Suspend
                        </button>
                      )}
                      {review.review_status === 'changes_requested' && (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() =>
                              handleAction(review.package_version_id, 'approve')
                            }
                            disabled={actionLoading === review.package_version_id}
                            className="btn btn-primary text-xs px-3 py-1"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() =>
                              handleAction(review.package_version_id, 'suspend')
                            }
                            disabled={actionLoading === review.package_version_id}
                            className="btn text-xs px-3 py-1 text-red-600 border border-red-200 hover:bg-red-50"
                          >
                            Suspend
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Change Request Modal */}
      {changeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Request Changes
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Select the categories that need attention and add specific notes.
            </p>

            {/* Category checkboxes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issue Categories
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CHANGE_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className={`text-left p-2.5 rounded-lg border text-sm transition-colors ${
                      selectedCategories.includes(cat.id)
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-xs">{cat.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{cat.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Notes textarea */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specific Notes
              </label>
              <textarea
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Describe what specific changes are needed..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setChangeModal(null)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={submitChangeRequest}
                disabled={selectedCategories.length === 0 && !changeNotes.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Change Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
